import { onBeforeUnmount, watch } from 'vue'

/**
 * Hides the Zendesk chat launcher (the floating circle) while an overlay is open.
 *
 * The launcher is a fixed iframe Zendesk renders at the bottom right with its own z-index, so it
 * floats above every dialog and, on a phone, sits exactly where a modal's footer buttons end up
 * (AIRLST-6120). Zendesk's documented messenger API hides and restores it; all overlays share one
 * counter so a drawer opened over a modal keeps the launcher away until both are gone.
 *
 * `window.zE` is only present when the chat is enabled for the company (see Headless/Chat.js), so
 * the calls are no-ops everywhere else, including the unit tests.
 */
let openOverlays = 0

const setLauncherVisible = (visible) => {
  if (typeof window === 'undefined' || typeof window.zE !== 'function') {
    return
  }

  window.zE('messenger', visible ? 'show' : 'hide')
}

/**
 * @param {() => boolean} isOpen Getter of the overlay's open state (usually `() => props.show`).
 */
export const useHideChatLauncherWhileOpen = (isOpen) => {
  let counted = false

  const sync = (open) => {
    if (open && !counted) {
      counted = true
      openOverlays += 1

      if (openOverlays === 1) {
        setLauncherVisible(false)
      }
    } else if (!open && counted) {
      counted = false
      openOverlays -= 1

      if (openOverlays === 0) {
        setLauncherVisible(true)
      }
    }
  }

  watch(isOpen, sync, { immediate: true })
  onBeforeUnmount(() => sync(false))
}

/** Test helper: forget every overlay counted so far. */
export const resetChatLauncherOverlays = () => {
  openOverlays = 0
}
